import { useState, useEffect, ChangeEvent, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { ISelectOption, SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { IAdminCatalogForUpdate } from 'APIPackage/AdminModule/interfaces/IAdminCatalogForUpdate';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './AdminCatalogsUpdate.module.scss';
import { getAdminCatalogsForUpdate } from '../selectors/adminSelectors';
import { adminGetCatalogsForUpdate, adminUploadCatalogs } from '../actions/adminAction';

interface IAdminCatalogsUpdateStoreProps {
  catalogs: IAdminCatalogForUpdate[];
}

const AdminCatalogsUpdateWithoutStore = ({ catalogs }: IAdminCatalogsUpdateStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedCatalog, setSelectedCatalog] = useState<ISelectOption | null>(null);
  const [catalogsOptions, setCatalogsOptions] = useState<ISelectOption[]>([]);
  const [selectedFile, setSelectedFile] = useState<File>();

  const handleSelectChange = useCallback((value: ISelectOption | null) => {
    setSelectedCatalog(value);
  }, []);

  const handleFileInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    setSelectedFile(file);
  }, []);

  const uploadCatalog = useCallback(() => {
    if (selectedCatalog && selectedFile) {
      dispatch(adminUploadCatalogs(Number(selectedCatalog.id), selectedFile));
    }
  }, [dispatch, selectedCatalog, selectedFile]);

  useEffect(() => {
    dispatch(adminGetCatalogsForUpdate());
  }, [dispatch]);

  useEffect(() => {
    const options: ISelectOption[] = catalogs.map((item: IAdminCatalogForUpdate) => ({
      id: item.id,
      label: item.name,
      value: item.acceptedFileFormat,
    }));

    setCatalogsOptions(options);
  }, [catalogs]);

  useEffect(() => {
    if (!selectedCatalog) {
      setSelectedFile(undefined);
    }
  }, [selectedCatalog]);

  return (
    <div className={styles.Wrapper}>
      <div className={styles.Title}>Загрузка справочников</div>
      <div className="shared-row__small-margin">
        <div className={styles.RowTitle}>Справочник</div>
        <SelectInput
          className={styles.Field}
          name="catalog"
          options={catalogsOptions}
          value={selectedCatalog}
          onChange={handleSelectChange}
        />
      </div>
      {selectedCatalog && (
        <div className="shared-row__small-margin">
          <label className="shared-file-button" htmlFor="file">
            Выберите файл
          </label>
          <input
            id="file"
            style={{ display: 'none' }}
            type="file"
            accept={selectedCatalog ? `.${selectedCatalog.value}` : undefined}
            name={selectedFile?.name}
            onChange={handleFileInputChange}
          />
        </div>
      )}
      <div className="shared-row__small-margin">
        {selectedFile && (
          <UploadFile fileName={selectedFile.name} fileSize={selectedFile.size} fileType={selectedFile.type} />
        )}
      </div>
      <div className={styles.SaveButtonBlock}>
        <Button
          disabled={!selectedCatalog || !selectedFile}
          color="primary"
          type="button"
          variant="contained"
          onClick={uploadCatalog}
          text="Загрузить справочник"
          id="uploadButton"
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IAdminCatalogsUpdateStoreProps => ({
  catalogs: getAdminCatalogsForUpdate(state),
});

export const AdminCatalogsUpdate = connect(mapStateToProps)(AdminCatalogsUpdateWithoutStore);
